import dayjs from "dayjs";

import type { ISwSettings, SnapshotInterval } from "app/@types/ISwSettings";
import { PackageName } from "app/common/constants/packageNames";
import { userRoleTitle } from "app/UtilitiesAndConstants/UtilityFunctions/userRoleUtils";

// --- BASE METHODS (no dependencies) ---
export const isTrial = (swSettings: ISwSettings): boolean => !!swSettings.components.Home?.resources.IsTrial;

export const isAfterTrial = (swSettings: ISwSettings): boolean => !!swSettings.components.Home?.resources.IsAfterTrial;

export const isCCWall = (swSettings: ISwSettings): boolean => !!swSettings.components.Home?.resources?.IsCCWall;

const isDMI = (swSettings: ISwSettings): boolean => swSettings.user.hasDM;

export const isNoTouch = (swSettings: ISwSettings): boolean => !!swSettings.components.Home?.resources.IsNoTouchUser;

export const isSI = (swSettings: ISwSettings): boolean => !!swSettings.user.hasSI;

export const isWebSolutionUser = (swSettings: ISwSettings): boolean =>
    !!swSettings.user.hasMR || !!swSettings.user.hasDM;

export const hasUserAddon = (swSettings: ISwSettings) => swSettings.components.Home?.resources.UserSeats >= 2;

export const getTrialSolutionName = (swSettings: ISwSettings): string =>
    swSettings?.components?.Home?.resources?.TrialSolutionName;

export const isAllowSwitchSolution = (swSettings: ISwSettings) =>
    !!getTrialSolutionName(swSettings) && isTraditionalTrialOrAfterTrialUser(swSettings);

export const hasAllowSwitchSolutionClaim = (swSettings: ISwSettings): boolean =>
    swSettings?.components?.Home?.resources?.AllowSwitchTrialSolution;

export const hasHistoricalDataAddon = (swSettings: ISwSettings) => {
    const snapshotInterval = swSettings?.current?.resources?.SnapshotInterval as SnapshotInterval;
    const startDate = new Date(snapshotInterval?.startdate).getTime();
    const endDate = new Date(snapshotInterval?.enddate).getTime();
    const dataIntervalLength = endDate - startDate;
    return dataIntervalLength >= 1000 * 60 * 60 * 24 * 364;
};

export const isAccountCreatedToday = (swSettings: ISwSettings): boolean => {
    const createDateObj = new Date(swSettings?.user?.createDate);
    const today = new Date();
    return createDateObj.toDateString() === today.toDateString();
};

export const isSkippedCC = (swSettings: ISwSettings): boolean =>
    !!swSettings?.user?.customData?.quiz?.ShouldGoToAfterTrial;

export const isPackageType = (swSettings: ISwSettings, plans: PackageName[]): boolean =>
    plans.some((planName) => swSettings.user.plan === String(planName));

export const isAnnual = (swSettings: ISwSettings): boolean => {
    const userPlanName = swSettings.user?.plan;
    return userPlanName?.toLowerCase().includes("annual") ?? false;
};

export const isMonthlyPaymentPeriod = (swSettings: ISwSettings) => {
    const userPlanName = swSettings.user.plan;
    return userPlanName.toLowerCase().includes("monthly");
};

export const hasReactivatedTrial = (swSettings: ISwSettings) =>
    !!swSettings.components.Home?.resources?.CCReactivatedTrial;

export const hasAddonsEnabled = (swSettings: ISwSettings) =>
    swSettings?.components?.Home?.resources?.CanPurchaseNTAddons === true;

export const getHasNTFlow = (swSettings: ISwSettings) => !!swSettings?.user?.customData?.NtFlow;

export const hasAccessToActivationHomepage2 = (swSettings: ISwSettings) =>
    Boolean(swSettings.components?.Activation?.resources?.["ActivationHomepage2.0"]);

export const hasAccessToOnboardingBot = (swSettings: ISwSettings) =>
    Boolean(swSettings.components?.OnboardingAgent?.resources?.IsAvailable);

export const isCompetitiveIntelligenceUser = (swSettings: ISwSettings) => {
    return (
        swSettings.user.plan === PackageName.CompetitiveIntelligenceAnnual ||
        swSettings.user.plan === PackageName.CompetitiveIntelligenceMonthly
    );
};

export const getUserPlan = (swSettings: ISwSettings): PackageName => swSettings.user?.plan as PackageName;

export const getFirstSubscriptionDate = (swSettings: ISwSettings): string | undefined =>
    swSettings.components?.Home?.resources?.PaidSubscriptionStartDate;

export const canUpgradeNoTouch = (swSettings: ISwSettings): string | undefined =>
    swSettings.components?.Home?.resources?.CanUpgradeNoTouch;

// --- FIRST-LEVEL METHODS (depend on base) ---

export const isCCTrialUser = (swSettings: ISwSettings) => isTrial(swSettings) && isCCWall(swSettings);

export const isTraditionalTrialUser = (swSettings: ISwSettings) => isTrial(swSettings) && !isCCWall(swSettings);

export const isTrialOrAfterTrialUser = (swSettings: ISwSettings) => isTrial(swSettings) || isAfterTrial(swSettings);

export const isTraditionalTrialOrAfterTrialUser = (swSettings: ISwSettings): boolean =>
    isTraditionalTrialUser(swSettings) || isAfterTrial(swSettings);

export const isNoTouchOrAfterTrial = (swSettings: ISwSettings): boolean =>
    isAfterTrial(swSettings) || isNoTouch(swSettings);

export const isNoTouchOrTrialOrAfterTrial = (swSettings: ISwSettings): boolean =>
    isTraditionalTrialOrAfterTrialUser(swSettings) || isNoTouch(swSettings);

export const isTouchUser = (swSettings: ISwSettings): boolean => !isNoTouchOrTrialOrAfterTrial(swSettings);

export const isDMINoTouchOrAfterTrial = (swSettings: ISwSettings): boolean =>
    isDMI(swSettings) && isNoTouchOrAfterTrial(swSettings);

export const isDMINoTouchOrTrialOrAfterTrial = (swSettings: ISwSettings): boolean =>
    isDMI(swSettings) && isNoTouchOrTrialOrAfterTrial(swSettings);

export const isDMITrial = (swSettings: ISwSettings): boolean => isDMI(swSettings) && isTraditionalTrialUser(swSettings);

export const isDMITrialOrAfterTrial = (swSettings: ISwSettings): boolean =>
    isDMI(swSettings) && isTraditionalTrialOrAfterTrialUser(swSettings);

export const isDMIAfterTrial = (swSettings: ISwSettings): boolean => isDMI(swSettings) && isAfterTrial(swSettings);

export const isSITrial = (swSettings: ISwSettings): boolean => isSI(swSettings) && isTraditionalTrialUser(swSettings);

export const isSIAfterTrial = (swSettings: ISwSettings): boolean => isSI(swSettings) && isAfterTrial(swSettings);

export const isReactivatedInTrial = (swSettings: ISwSettings) =>
    hasReactivatedTrial(swSettings) && isCCTrialUser(swSettings);

export const isRoleAdmin = (swSettings: ISwSettings) => swSettings?.user.userRole === userRoleTitle.ADMIN;

// --- OTHER UTILITY METHODS ---
export const getDaysLeft = (swSettings: ISwSettings): number => {
    const { TrialExpirationDate: trialExpirationDate, TrialEndDate: trialEndDate } =
        swSettings.components.Home?.resources ?? {};
    const trialDateStr = trialExpirationDate ?? trialEndDate;

    if (!trialDateStr) {
        return 0;
    }

    const trialExpDate = dayjs.utc(trialDateStr).startOf("day");
    if (!trialExpDate.isValid()) {
        return 0;
    }

    const today = dayjs.utc().startOf("day");
    let diff = trialExpDate.diff(today, "day");

    // add an extra day to calculation if TrialExpirationDate was used
    if (trialExpirationDate) {
        diff += 1;
    }

    return Math.max(diff, 0);
};

export const getDaysAfterTrial = (swSettings: ISwSettings): number => {
    const { TrialExpirationDate: trialExpirationDate, TrialEndDate: trialEndDate } =
        swSettings.components.Home?.resources ?? {};
    const trialDateStr = trialExpirationDate ?? trialEndDate;

    if (!trialDateStr) {
        return 0;
    }

    const trialExpDate = dayjs.utc(trialDateStr).startOf("day");

    if (!trialExpDate.isValid()) {
        return 0;
    }

    const today = dayjs.utc().startOf("day");
    let diff = today.diff(trialExpDate, "day");

    if (trialExpirationDate) {
        diff -= 1;
    }

    return Math.max(diff, 0);
};

export const isNtFlowMcp = (swSettings: ISwSettings) => swSettings?.user?.customData?.NtFlow === "mcp";

export const isWincherNtFlow = (swSettings: ISwSettings) => !!swSettings?.user?.customData?.NtFlow?.includes("wincher");

export const isWincherUser = (swSettings: ISwSettings) =>
    !!swSettings?.user?.customData?.WincherToken || isWincherNtFlow(swSettings);

export const getWorkspaceName = (swSettings: ISwSettings): string => {
    const woorkspaceType = swSettings?.components?.Workspaces?.resources?.WorkspaceType as string | string[];
    let workspace = Array.isArray(woorkspaceType) ? woorkspaceType[0] : woorkspaceType;

    if (!workspace || /hook/i.test(workspace)) {
        workspace = swSettings?.components?.Home?.resources?.TrialWorkspace as string;
    }
    return !workspace ? "" : workspace.trim().toLowerCase();
};

export const isMcpNt = (swSettings: ISwSettings) =>
    getUserPlan(swSettings) === PackageName.GrowAnnual || getUserPlan(swSettings) === PackageName.GrowMonthly;

export const isMcpTrialOrAfterTrial = (swSettings: ISwSettings) =>
    isNtFlowMcp(swSettings) && isTraditionalTrialOrAfterTrialUser(swSettings);

export const isMcpNtOrTrialOrAfterTrial = (swSettings: ISwSettings) =>
    isMcpNt(swSettings) || isMcpTrialOrAfterTrial(swSettings);
